import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Newsletter, Contribution, SubscribeRequest } from '../types';
import { apiWorker, subscribeToNewsletter } from '../api/worker';
import toast from 'react-hot-toast';

export const useNewsletters = (nodeId: string, recursive: boolean = true) => {
  return useQuery({
    queryKey: ['newsletters', nodeId, recursive],
    queryFn: () => 
      apiWorker.get(`/api/nodes/${nodeId}/newsletters`, {
        params: { recursive }
      }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId,
  });
};

export const useNewsletter = (id: string) => {
  return useQuery({
    queryKey: ['newsletter', id],
    queryFn: () => apiWorker.get(`/api/newsletters/${id}`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!id,
  });
};

export const useCreateNewsletter = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => 
      apiWorker.post(`/api/nodes/${nodeId}/newsletters`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['newsletters', nodeId] });
      toast.success('Draft newsletter created');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Creation failed');
    }
  });
};

export const useUpdateNewsletterSections = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { sections: any[]; version: number }) => 
      apiWorker.patch(`/api/newsletters/${id}/sections`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter', id] });
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        toast.error('Sync conflict: This newsletter has been updated elsewhere. Please refresh.');
      } else {
        toast.error('Failed to save content');
      }
    }
  });
};

export const useUpdateNewsletterMetadata = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Newsletter>) => 
      apiWorker.patch(`/api/newsletters/${id}/metadata`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter', id] });
      toast.success('Metadata saved');
    }
  });
};

export const usePublishNewsletter = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => 
      apiWorker.post(`/api/newsletters/${id}/publish`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter', id] });
      toast.success('Newsletter enqueued for publishing');
    },
    onError: () => {
      toast.error('Failed to start publishing');
    }
  });
};

export const useNewsletterStats = (id: string) => {
  return useQuery({
    queryKey: ['newsletter-stats', id],
    queryFn: () => apiWorker.get(`/api/newsletters/${id}/stats`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!id,
  });
};

export const useSubscribeToNewsletter = (newsletterId: string) => {
  return useMutation({
    mutationFn: (email: string) => {
      const payload: SubscribeRequest = { email };
      return subscribeToNewsletter(newsletterId, payload);
    },
    onSuccess: () => {
      toast.success('Subscription successful! Check your email to confirm.');
    },
    onError: () => {
      toast.error('Failed to subscribe to newsletter');
    }
  });
};

export const useContributions = (nodeId: string) => {
  return useQuery({
    queryKey: ['contributions', nodeId],
    queryFn: () => 
      apiWorker.get(`/api/communications/${nodeId}/contributions`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId,
  });
};

export const useApproveContribution = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => 
      apiWorker.post(`/api/communications/contributions/${id}/approve`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contributions', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['contributions', 'ready', nodeId] });
      toast.success('Contribution approved for newsletter');
    },
    onError: () => {
      toast.error('Failed to approve contribution');
    }
  });
};

export const useRejectContribution = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => 
      apiWorker.post(`/api/communications/contributions/${id}/reject`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contributions', nodeId] });
      toast.success('Contribution rejected');
    },
    onError: () => {
      toast.error('Failed to reject contribution');
    }
  });
};

export const useSubmitContribution = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Contribution>) => 
      apiWorker.post(`/api/communications/contributions`, { ...data, nodeId }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contributions', nodeId] });
      toast.success('Contribution submitted! It will appear in the newsletter once reviewed by an admin.');
    },
    onError: () => {
      toast.error('Failed to submit contribution');
    }
  });
};

export const useReadyContributions = (nodeId: string) => {
  return useQuery({
    queryKey: ['contributions', 'ready', nodeId],
    queryFn: () => 
      apiWorker.get(`/api/communications/${nodeId}/contributions/ready`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId,
  });
};

export const useUploadNewsletterImage = (newsletterId: string) => {
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiWorker.post(`/api/newsletters/${newsletterId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(r => r.data);
    }
  });
};
